import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Modal, Badge, Spinner, Row, Col, Alert, Card } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import { packageAPI } from '../services/api';
import { toast } from 'react-toastify';

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '', duration_days: '', credit_cost: '', reset_limit: '', max_keywords: '', max_export: '', is_trial: false, is_active: true
  });

  useEffect(() => { fetchPackages(); }, []);

  const fetchPackages = async () => {
    try {
      const response = await packageAPI.getAll();
      setPackages(response.data.data);
    } catch (error) { toast.error('Failed to load packages'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, max_keywords: formData.max_keywords || null, max_export: formData.max_export || null };
      if (editingPackage) {
        await packageAPI.update(editingPackage.id, data);
        toast.success('Package updated');
      } else {
        await packageAPI.create(data);
        toast.success('Package created');
      }
      setShowModal(false);
      fetchPackages();
      resetForm();
    } catch (error) { toast.error('Operation failed'); }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({ ...pkg, max_keywords: pkg.max_keywords || '', max_export: pkg.max_export || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this package?')) {
      try {
        await packageAPI.delete(id);
        toast.success('Package deleted');
        fetchPackages();
      } catch (error) { toast.error('Delete failed'); }
    }
  };

  const resetForm = () => {
    setEditingPackage(null);
    setFormData({ name: '', duration_days: '', credit_cost: '', reset_limit: '', max_keywords: '', max_export: '', is_trial: false, is_active: true });
  };

  if (loading) return <Spinner animation="border" />;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>License Packages</h3>
        <Button variant="primary" onClick={() => { resetForm(); setShowModal(true); }}><FaPlus /> Add Package</Button>
      </div>

      <Row>
        {packages.map((pkg) => (
          <Col md={6} lg={4} key={pkg.id} className="mb-4">
            <Card className={pkg.is_active ? '' : 'border-secondary'}>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>{pkg.name}</span>
                {pkg.is_trial && <Badge bg="warning">Trial</Badge>}
                {!pkg.is_active && <Badge bg="secondary">Inactive</Badge>}
              </Card.Header>
              <Card.Body>
                <Table size="sm" borderless>
                  <tbody>
                    <tr><td>Duration:</td><td>{pkg.duration_days} days</td></tr>
                    <tr><td>Credit Cost:</td><td>{pkg.credit_cost} credits</td></tr>
                    <tr><td>Device Resets:</td><td>{pkg.reset_limit}</td></tr>
                    <tr><td>Max Keywords:</td><td>{pkg.max_keywords || 'Unlimited'}</td></tr>
                    <tr><td>Max Export:</td><td>{pkg.max_export || 'Unlimited'}</td></tr>
                  </tbody>
                </Table>
                <div className="d-flex gap-2">
                  <Button variant="info" size="sm" onClick={() => handleEdit(pkg)}><FaEdit /></Button>
                  <Button variant="danger" size="sm" onClick={() => handleDelete(pkg.id)}><FaTrash /></Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>{editingPackage ? 'Edit' : 'Create'} Package</Modal.Title></Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Name</Form.Label><Form.Control required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Duration (days)</Form.Label><Form.Control type="number" required value={formData.duration_days} onChange={(e) => setFormData({...formData, duration_days: e.target.value})} /></Form.Group></Col>
            </Row>
            <Row>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Credit Cost</Form.Label><Form.Control type="number" required value={formData.credit_cost} onChange={(e) => setFormData({...formData, credit_cost: e.target.value})} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Reset Limit</Form.Label><Form.Control type="number" required value={formData.reset_limit} onChange={(e) => setFormData({...formData, reset_limit: e.target.value})} /></Form.Group></Col>
            </Row>
            <Row>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Max Keywords (empty = unlimited)</Form.Label><Form.Control type="number" value={formData.max_keywords} onChange={(e) => setFormData({...formData, max_keywords: e.target.value})} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Max Export (empty = unlimited)</Form.Label><Form.Control type="number" value={formData.max_export} onChange={(e) => setFormData({...formData, max_export: e.target.value})} /></Form.Group></Col>
            </Row>
            <Row>
              <Col md={6}><Form.Check type="checkbox" label="Is Trial Package" checked={formData.is_trial} onChange={(e) => setFormData({...formData, is_trial: e.target.checked})} /></Col>
              <Col md={6}><Form.Check type="checkbox" label="Active" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} /></Col>
            </Row>
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button variant="primary" type="submit">Save</Button></Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Packages;
